import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Video, Music, X, Loader2, CheckCircle, Play, Download, Eye, Plus, RefreshCw, UserCheck } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { blobToFile } from '../../utils/mediaUtils';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, inputNeed } from '../../utils/nodeRequirements';

// sync_mode: qué hacer cuando audio y video tienen distinta duración
const SYNC_MODES = [
  { value: 'loop', label: 'Loop (repite el más corto)' },
  { value: 'cut_off', label: 'Cortar al más corto' },
  { value: 'bounce', label: 'Bounce (ida y vuelta)' },
  { value: 'silence', label: 'Silencio al terminar el audio' },
  { value: 'remap', label: 'Remap (remapeo temporal)' },
];

const VIDEO_ACCEPT = 'video/mp4,video/mov,video/webm';
const AUDIO_ACCEPT = 'audio/mpeg,audio/mp3,audio/wav,audio/m4a,audio/aac,audio/ogg';

const LipSyncNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [syncMode, setSyncMode] = useState(data.syncMode || 'loop');
  const [temperature, setTemperature] = useState(data.temperature ?? 0.5);
  const [activeSpeaker, setActiveSpeaker] = useState(!!data.activeSpeaker);
  const [videoLocal, setVideoLocal] = useState(data.videoLocal || null);
  const [audioLocal, setAudioLocal] = useState(data.audioLocal || null);

  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const edges = useWorkflowStore((state) => state.edges);
  const nodes = useWorkflowStore((state) => state.nodes);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);

  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const { openVideoPreview } = useMediaPreview();

  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';

  const connData = useCallback((handleId) => {
    const edge = edges.find(e => e.target === id && e.targetHandle === handleId);
    if (!edge) return null;
    const src = nodes.find(n => n.id === edge.source);
    return src?.data?.hasOutput ? src.data : null;
  }, [edges, nodes, id]);

  const videoConn = connData('inputVideo');
  const audioConn = connData('inputAudio');
  const video = videoConn || videoLocal;
  const audio = audioConn || audioLocal;

  useEffect(() => {
    updateNodeData(id, { syncMode, temperature, activeSpeaker, videoLocal, audioLocal });
  }, [syncMode, temperature, activeSpeaker, videoLocal, audioLocal]); // eslint-disable-line

  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => setChronometer((prev) => prev + 1), 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const pickMedia = useCallback((which) => {
    const isVideo = which === 'video';
    setSourcePickerHandler((result) => {
      const entry = result.file
        ? { file: result.file, preview: URL.createObjectURL(result.file), name: result.file.name }
        : { file: null, preview: result.url, name: result.fileName, fromGallery: true };
      if (isVideo) setVideoLocal(entry); else setAudioLocal(entry);
    });
    openSourcePicker(id, [which], isVideo ? VIDEO_ACCEPT : AUDIO_ACCEPT);
  }, [id, openSourcePicker]);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);

  const handleProcess = useCallback(async () => {
    if (!video || !audio) return false;
    resetExec();

    const files = [];
    const params = {
      sync_mode: syncMode,
      temperature: Number(temperature),
      active_speaker: activeSpeaker,
    };

    const attach = async (src, key, mime) => {
      let file = src.file;
      if (!file) {
        const url = src.outputUrl || src.preview;
        if (url?.startsWith('http')) { params[key] = url; return; }
        if (url) file = await blobToFile(url, src.name || `${key}.bin`, mime);
      }
      if (file) files.push({ key, file });
    };
    await attach(video, 'video_url', 'video/mp4');
    await attach(audio, 'audio_url', 'audio/mpeg');

    const result = await processNode({
      resultType: 'lipsync',
      files,
      params,
    });

    if (result?.outputUrl) {
      setReconfigure(false);
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        outputType: 'video',
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'video',
        tool: 'lipSync',
        filename: `lipsync_${Date.now()}.mp4`,
        url: result.outputUrl,
        prompt: `Lip-sync (${syncMode})`,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, video, audio, syncMode, temperature, activeSpeaker,
      updateNodeData, addAsset, processNode, resetExec, setReconfigure]);

  const handleRegenerate = useCallback(() => {
    updateNodeData(id, { hasOutput: false, outputUrl: null });
    resetExec();
  }, [id, updateNodeData, resetExec]);

  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  const canProcess = !!video && !!audio;

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([inputNeed('video', !!video), inputNeed('audio', !!audio)]);

  return (
    <div className={`node-container lipSync-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`} data-testid="lipsync-node">
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />

      {/* Input handles: video + audio */}
      <Handle type="target" position={Position.Left} id="inputVideo" className="node-handle input-handle video-input-handle" style={{ top: '25%' }} data-testid="lipsync-input-video" />
      <Handle type="target" position={Position.Left} id="inputAudio" className="node-handle input-handle audio-handle" style={{ top: '45%' }} data-testid="lipsync-input-audio" />

      {/* Header */}
      <div className="node-header lipSync-header">
        <div className="node-header-left">
          <UserCheck size={16} />
          <span>LipSinc</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)} data-testid="delete-lipsync">
          <X size={14} />
        </button>
      </div>

      {/* Content */}
      <div className="node-content lipSync-content">
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">{formatTime(chronometer)}</div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>
                {execStatus === 'uploading' ? 'Subiendo archivos...' :
                 execStatus === 'queued' ? 'En cola...' :
                 'Sincronizando labios...'}
              </span>
            </div>
          </div>
        ) : !showForm ? (
          /* ===== RESULT VIEW ===== */
          <div className="node-result">
            <video
              src={data.outputUrl}
              controls
              className="node-result-video clickable"
              onClick={() => openVideoPreview(data.outputUrl, 'LipSinc')}
            />
            <div className="node-result-actions">
              <button className="node-mini-preview-btn" onClick={() => openVideoPreview(data.outputUrl, 'LipSinc')} data-testid="lipsync-preview-btn">
                <Eye size={12} /> Ver
              </button>
              <button className="node-mini-download-btn" onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `lipsync_${Date.now()}.mp4`); }} data-testid="lipsync-download-btn">
                <Download size={12} /> Descargar
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="video" label="lipSync" testIdPrefix="lipsync-timeline" />
            </div>
            <div className="nano-regen-section">
              <button className="nano-regen-btn lipSync-regen-btn" onClick={handleRegenerate} data-testid="lipsync-regen-btn">
                <RefreshCw size={12} />
                Regenerar
              </button>
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="lipsync-reconfigure-btn" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} />
              <span>Lip-sync listo</span>
            </div>
          </div>
        ) : (
          <>
            {data.outputUrl && reconfigure && (
              <ReconfigureNotice
                previewUrl={null}
                onCancel={() => setReconfigure(false)}
                testId="lipsync-reconfigure-notice"
              />
            )}

            {/* Video de entrada */}
            <div className="nano-images-section">
              <span className="nano-section-label">
                <Video size={12} />
                Video con el hablante *
              </span>
              <div className="nano-images-row">
                {video ? (
                  <div className="nano-img-thumb lipSync-media-thumb" title={video.name || 'video'}>
                    <Video size={16} />
                    {!videoConn && (
                      <button className="nano-clear-btn" onClick={() => setVideoLocal(null)} data-testid="lipsync-clear-video">
                        <X size={8} />
                      </button>
                    )}
                  </div>
                ) : (
                  <button className="nano-add-img-btn" onClick={() => pickMedia('video')} title="Subir o elegir video de la galería" data-testid="lipsync-add-video">
                    <Plus size={14} />
                  </button>
                )}
              </div>
              {videoConn && <span className="lipSync-conn-note">Video conectado</span>}
            </div>

            {/* Audio de entrada */}
            <div className="nano-images-section">
              <span className="nano-section-label">
                <Music size={12} />
                Audio con la voz *
              </span>
              <div className="nano-images-row">
                {audio ? (
                  <div className="nano-img-thumb lipSync-media-thumb" title={audio.name || 'audio'}>
                    <Music size={16} />
                    {!audioConn && (
                      <button className="nano-clear-btn" onClick={() => setAudioLocal(null)} data-testid="lipsync-clear-audio">
                        <X size={8} />
                      </button>
                    )}
                  </div>
                ) : (
                  <button className="nano-add-img-btn" onClick={() => pickMedia('audio')} title="Subir o elegir audio de la galería" data-testid="lipsync-add-audio">
                    <Plus size={14} />
                  </button>
                )}
              </div>
              {audioConn && <span className="lipSync-conn-note">Audio conectado</span>}
            </div>

            {/* Opciones */}
            <div className="nano-options-row lipSync-options">
              <div className="nano-option">
                <span className="nano-option-label">Modo sync</span>
                <select value={syncMode} onChange={(e) => setSyncMode(e.target.value)} className="nano-select" data-testid="lipsync-sync-mode">
                  {SYNC_MODES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="eleV2-slider-row lipSync-temp-row">
              <span className="nano-option-label">Expresividad</span>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value))}
                className="videoSound-slider"
                data-testid="lipsync-temperature"
              />
              <span className="videoSound-duration-value">{temperature}</span>
            </div>

            <div className="banana2pro-toggles">
              <label className="banana2pro-toggle lipSync-toggle" title="En videos con varias personas, sincroniza sólo a quien está hablando">
                <input type="checkbox" checked={activeSpeaker} onChange={(e) => setActiveSpeaker(e.target.checked)} data-testid="lipsync-active-speaker" />
                <UserCheck size={11} />
                <span>Hablante activo</span>
              </label>
            </div>

            <button
              className="node-process-btn lipSync-process-btn"
              onClick={handleProcess}
              disabled={!canProcess}
              title={requirementsTitle(runMissing)}
              data-testid="lipsync-process-btn"
            >
              <Play size={14} />
              {data.outputUrl && reconfigure ? 'Regenerar con cambios' : 'Sincronizar labios'}
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}

        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle video-output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

LipSyncNode.displayName = 'LipSyncNode';

export default LipSyncNode;
